// Clasificador de ámbito de cámaras: estatal (red C5) vs municipal
// (ayuntamiento) vs otro (privadas/eventos/aeropuertos).
//
// SOLO configuración explícita del CRUD de Grupos — sin heurísticas
// (decisión del stakeholder, jul 2026: un grupo sin configurar no pinta
// nada en el mapa):
//   - `group.attributes.ambito` ('estatal' | 'municipal' | 'otro') define
//     la clasificación (filtro del toolbar, drawer) y el color de paleta.
//   - `group.attributes.color` (ColorPicker) define el color de la placa;
//     pinta incluso sin ámbito y gana sobre el color de paleta.
//   - Sin ambito ni color → ownership '' y color '' → sin placa ni velo,
//     y exenta del filtro por ámbito (siempre visible).
//
// El registry de grupos llega tarde en el boot (onComplete del
// initial-loader, DESPUÉS de que las cámaras ya se ingestaron). Por eso la
// resolución es lazy por groupId (llamada desde monitorToFeature en cada
// rebuild) y NO se persiste en el monitor: mientras el registry está vacío
// devuelve '' (desconocido → sin anillo, visible siempre) y el
// _rebuildSourceWithFilters del 'complete' recomputa ya con grupos.

import { getActiveMarkerPalette } from '../theming/apply-markers'

export const CAMERA_OWNERSHIPS = ['estatal', 'municipal', 'otro']

export const CAMERA_OWNERSHIP_LABELS = {
  estatal: 'Estatal',
  municipal: 'Municipal',
  otro: 'Otras',
}

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/

// Switch maestro de la modalidad (jul 2026: apagada hasta autorización
// formal). Opt-in por build: solo VITE_CAMERA_OWNERSHIP=true la enciende;
// ausente o cualquier otro valor = apagada. Apagar NO toca los datos: los
// attributes ambito/color de los grupos quedan intactos y el CRUD de
// Grupos los sigue capturando — reactivar es definir la var y redesplegar.
export function isCameraOwnershipEnabled() {
  return import.meta.env.VITE_CAMERA_OWNERSHIP === 'true'
}

// Suaviza un color custom del CRUD para integrarlo al lenguaje visual de
// las placas: -35% de saturación y luminosidad acotada al rango medio.
// Los tokens de paleta ya nacen desaturados; sin esto, un admin que elige
// un morado 100% saturado obtiene una placa que grita sobre las demás
// (y percibe que "no tiene transparencia" aunque la opacity sea la misma).
// El alpha del picker (#RRGGBBAA) se descarta — la opacidad la fija la layer.
function softenHex(hex) {
  let h = hex.replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  let hue = 0
  let s = 0
  if (d > 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    if (max === r) hue = 60 * (((g - b) / d) + (g < b ? 6 : 0))
    else if (max === g) hue = 60 * ((b - r) / d + 2)
    else hue = 60 * ((r - g) / d + 4)
  }
  const s2 = s * 0.65
  const l2 = Math.min(0.68, Math.max(0.38, l))
  // HSL → RGB
  const c = (1 - Math.abs(2 * l2 - 1)) * s2
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = l2 - c / 2
  let rgb
  if (hue < 60) rgb = [c, x, 0]
  else if (hue < 120) rgb = [x, c, 0]
  else if (hue < 180) rgb = [0, c, x]
  else if (hue < 240) rgb = [0, x, c]
  else if (hue < 300) rgb = [x, 0, c]
  else rgb = [c, 0, x]
  return '#' + rgb.map((v) => {
    const n = Math.round((v + m) * 255)
    return (n < 16 ? '0' : '') + n.toString(16)
  }).join('')
}

let groupRegistry = null
let cache = {}
let colorCache = {}
let sensorColorCache = {}

// MapV2 llama esto en onComplete del initial-loader (mismo momento en que
// congela this.groups). Invalida el cache por si el registry se refresca.
export function setCameraOwnershipGroups(groups) {
  groupRegistry = groups || null
  cache = {}
  colorCache = {}
  sensorColorCache = {}
}

// `group.attributes.color` (ColorPicker del CRUD de Grupos) ya armonizado,
// o '' si el grupo no lo declara o no es un hex válido.
function customGroupColor(group) {
  const custom = group && group.attributes && group.attributes.color
  if (custom && HEX_RE.test(String(custom).trim())) return softenHex(String(custom).trim())
  return ''
}

export function resolveCameraOwnership(group) {
  if (!group) return ''
  const attr = group.attributes && group.attributes.ambito
  if (attr) {
    const v = String(attr).toLowerCase().trim()
    if (CAMERA_OWNERSHIPS.indexOf(v) !== -1) return v
  }
  return ''
}

// Resolución memoizada por groupId contra el registry global. '' significa
// "aún no sé" (registry no cargado): sin anillo y exento del filtro por
// ámbito hasta que el boot complete.
export function resolveCameraOwnershipByGroupId(groupId) {
  if (!isCameraOwnershipEnabled()) return ''
  if (!groupRegistry || groupId == null) return ''
  if (cache[groupId] !== undefined) return cache[groupId]
  const own = resolveCameraOwnership(groupRegistry[groupId])
  cache[groupId] = own
  return own
}

// Color de la placa para un grupo. Prioridad:
//   1. `group.attributes.color` (hex del ColorPicker del CRUD de Grupos) —
//      pinta incluso si el grupo no tiene ámbito declarado.
//   2. Token `camara-<ambito>` de la paleta del tema, si hay ámbito.
//   3. '' — sin configuración explícita: la placa y el velo no se dibujan
//      (las layers filtran por ownershipColor != '').
export function resolveCameraOwnershipColorByGroupId(groupId) {
  if (!isCameraOwnershipEnabled()) return ''
  if (!groupRegistry || groupId == null) return ''
  if (colorCache[groupId] !== undefined) return colorCache[groupId]
  let color = customGroupColor(groupRegistry[groupId])
  if (!color) {
    const own = resolveCameraOwnershipByGroupId(groupId)
    if (own) {
      const pal = (getActiveMarkerPalette() || {}).paleta || {}
      color = pal['camara-' + own] || pal['camara-normal'] || ''
    }
  }
  colorCache[groupId] = color
  return color
}

// Color de la placa para un botón de pánico (sensor), por su grupo. Misma
// placa/velo que las cámaras, pero SOLO desde `group.attributes.color`: los
// grupos de sensores no tienen ámbito ni token de paleta. Siempre activo
// (sin switch): un grupo sin color no pinta nada, así que sin configuración
// el mapa queda igual que antes.
export function resolveSensorGroupColorByGroupId(groupId) {
  if (!groupRegistry || groupId == null) return ''
  if (sensorColorCache[groupId] !== undefined) return sensorColorCache[groupId]
  const color = customGroupColor(groupRegistry[groupId])
  sensorColorCache[groupId] = color
  return color
}
